/**
 * Icon Loader for Wheel of Fortune
 * 
 * Loads monochrome white PNG icons from curated library
 * Icons are tintable via PIXI sprite.tint
 */

import * as PIXI from 'pixi.js';
import type { IconKey } from '../runtime/types';

const ICON_BASE_PATH = '/runtime-assets/games/wheel_of_fortune/v1/icons';

const ICON_PATHS: Record<Exclude<IconKey, 'none'>, string> = {
  gift: `${ICON_BASE_PATH}/icon_gift.png`,
  diamond: `${ICON_BASE_PATH}/icon_diamond.png`,
  star: `${ICON_BASE_PATH}/icon_star.png`,
  coin: `${ICON_BASE_PATH}/icon_coin.png`,
  heart: `${ICON_BASE_PATH}/icon_heart.png`,
  trophy: `${ICON_BASE_PATH}/icon_trophy.png`,
  coupon: `${ICON_BASE_PATH}/icon_coupon.png`,
  percent: `${ICON_BASE_PATH}/icon_percent.png`,
  shipping: `${ICON_BASE_PATH}/icon_shipping.png`,
  nowin: `${ICON_BASE_PATH}/icon_nowin.png`,
};

export type IconTextures = Partial<Record<IconKey, PIXI.Texture>>;

/**
 * Load all icon textures
 */
export async function loadIconTextures(): Promise<IconTextures> {
  console.log('[IconLoader] Loading icon library...');
  
  const textures: IconTextures = {};
  const iconKeys = Object.keys(ICON_PATHS) as Array<Exclude<IconKey, 'none'>>;
  
  try {
    // Load all icons in parallel
    const loadPromises = iconKeys.map(async (key) => {
      try {
        const texture = await PIXI.Assets.load<PIXI.Texture>(ICON_PATHS[key]);
        textures[key] = texture;
        return { key, success: true };
      } catch (error) {
        console.warn(`[IconLoader] Failed to load icon: ${key}`, error);
        return { key, success: false };
      }
    });
    
    const results = await Promise.all(loadPromises);
    const loaded = results.filter(r => r.success).length;
    
    console.log(`[IconLoader] ✅ Loaded ${loaded}/${iconKeys.length} icons`);
    return textures;
    
  } catch (error) {
    console.error('[IconLoader] ❌ Failed to load icons:', error);
    return textures;
  }
}

/**
 * Get auto-contrast color for icon based on segment background
 * @param segmentColor - Segment background color (hex string)
 * @returns Tint color for high contrast
 */
export function getAutoContrastColor(segmentColor: string): number {
  // Parse hex color
  const hex = segmentColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate perceived brightness (YIQ formula)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Return white for dark backgrounds, dark navy for light backgrounds
  return brightness > 128 ? 0x1a1a2e : 0xffffff;
}

/**
 * Parse icon color config to PIXI tint value
 * @param iconColor - 'auto' or hex string
 * @param segmentColor - Segment background color for auto contrast
 */
export function parseIconColor(iconColor: string, segmentColor: string): number {
  if (iconColor === 'auto') {
    return getAutoContrastColor(segmentColor);
  }
  
  // Parse hex color
  const hex = iconColor.replace('#', '');
  return parseInt(hex, 16);
}

/**
 * Available icon keys for UI dropdown
 */
export const ICON_OPTIONS: Array<{ value: IconKey; label: string }> = [
  { value: 'none', label: 'No Icon' },
  { value: 'gift', label: '🎁 Gift Box' },
  { value: 'diamond', label: '💎 Diamond' },
  { value: 'star', label: '⭐ Star' },
  { value: 'coin', label: '🪙 Coin' },
  { value: 'heart', label: '❤️ Heart' },
  { value: 'trophy', label: '🏆 Trophy' },
  { value: 'coupon', label: '🎟️ Coupon' },
  { value: 'percent', label: '💯 Percent' },
  { value: 'shipping', label: '🚚 Shipping' },
  { value: 'nowin', label: '❌ No Win' },
];
